import { Button, ButtonGroup, Content, Heading } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { type PropsWithChildren } from 'react';

import { SchedulePopover } from './SchedulePopover';

interface ExpressSchedulePopoverWrapperProps {
	isOpen: boolean;
	hasValidInstagramConnection: boolean;
	beginExpressConnection: () => void;
	scheduleDraft: string;
	onScheduleDraftChange: (value: string) => void;
	onCancel: () => void;
	onConfirm: () => void | Promise<void>;
	/** The time this post is already scheduled for, formatted, when the owner has
	 * asked for that same time again. Absent means there is nothing to confirm. */
	duplicateScheduleTime?: string | null;
	onConfirmDuplicate?: () => void | Promise<void>;
}

export const ExpressSchedulePopoverWrapper = ({
	children,
	isOpen,
	hasValidInstagramConnection,
	beginExpressConnection,
	scheduleDraft,
	onScheduleDraftChange,
	onCancel,
	onConfirm,
	duplicateScheduleTime,
	onConfirmDuplicate,
}: PropsWithChildren<ExpressSchedulePopoverWrapperProps>) => {
	const duplicateBranch = duplicateScheduleTime ? (
		<div className="flex flex-col gap-6 p-6">
			<Heading
				styles={style({
					font: 'title',
					color: 'heading',
					fontSize: 'title-lg',
					fontWeight: 'bold',
				})}
			>
				Already Scheduled
			</Heading>
			<Content>
				{`This post is already scheduled for ${duplicateScheduleTime}. Schedule it again?`}
			</Content>
			<ButtonGroup UNSAFE_className="justify-center pt-2">
				<Button variant="secondary" type="button" onPress={onCancel}>
					Cancel
				</Button>
				<Button variant="primary" onPress={() => void onConfirmDuplicate?.()}>
					Schedule again
				</Button>
			</ButtonGroup>
		</div>
	) : undefined;

	const connectionNeededBranch = !hasValidInstagramConnection ? (
		<div className="flex flex-col gap-6 p-6">
			<Heading
				styles={style({
					font: 'title',
					color: 'heading',
					fontSize: 'title-lg',
					fontWeight: 'bold',
				})}
			>
				Connection Needed
			</Heading>
			<Content>Link Adobe Express to schedule this post.</Content>
			<ButtonGroup UNSAFE_className="justify-center pt-2">
				<Button variant="secondary" type="button" onPress={onCancel}>
					Cancel
				</Button>
				<Button variant="primary" onPress={beginExpressConnection}>
					Connect Express
				</Button>
			</ButtonGroup>
		</div>
	) : undefined;

	return (
		<SchedulePopover
			isOpen={isOpen}
			scheduleDraft={scheduleDraft}
			onScheduleDraftChange={onScheduleDraftChange}
			onCancel={onCancel}
			onConfirm={onConfirm}
			heading="Schedule post"
			alternateBranch={connectionNeededBranch ?? duplicateBranch}
		>
			{children}
		</SchedulePopover>
	);
};
